import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Table from '../../../components/organisms/Table';
import WarningMessage from '../../../components/utilities/WarningMessage';
import CopyToClipboardButton from '../../../components/molecules/CopyToClipboardButton';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import { getOrgAgencies } from '../../../selectors/agencies';
import { getAgencyId, getIsAgency, getUserData } from '../../../selectors/user';
import { SAN_PARTNERS } from '../../consts';
import { partnerShape } from '../types';
import { getAgencyCampaignPrefix, getSanAgencyIds, isAgencyViewingSan } from './agencySetup';
import css from './Agencies.css';

const TRANSLATE_PREFIX = 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.AGENCIES';

const HOW_TO_STEP_COUNT = 5;
const HOW_TO_STEPS = Array.from({ length: HOW_TO_STEP_COUNT }, (_, index) => index + 1);
// The step whose text is followed by a code-styled example campaign name.
const EXAMPLE_STEP = 4;
const EXAMPLE_CAMPAIGN_SUFFIX = 'Summer_Sale_2024';

// Custom Table cells must render the <td> themselves (like the default TableCell widget).
const AgencyIdCell = ({ value, className }) => (
    <td className={className}>
        <span className={css.agencyId}>{value}</span>
    </td>
);
AgencyIdCell.propTypes = { value: PropTypes.string, className: PropTypes.string };

const PrefixCell = ({ value, className }) => (
    <td className={className}>
        <span className={css.prefix}>{value}</span>
    </td>
);
PrefixCell.propTypes = { value: PropTypes.string, className: PropTypes.string };

const ActionsCell = ({ row, onRemove, isReadonly, className }) => (
    <td className={className}>
        <div className={css.actions}>
            <CopyToClipboardButton textToCopy={row.prefix} tooltip={`${TRANSLATE_PREFIX}.COPY_TOOLTIP`} />
            {!isReadonly && (
                <Icon name="x" variant={IconVariants.ERROR} size={IconSizes.MEDIUM} onClick={() => onRemove(row.id)} />
            )}
        </div>
    </td>
);
ActionsCell.propTypes = {
    row: PropTypes.shape({ id: PropTypes.string, prefix: PropTypes.string }),
    onRemove: PropTypes.func,
    isReadonly: PropTypes.bool,
    className: PropTypes.string,
};

// Rendered as the Agencies shelf tab. The selected agency ids are owned by the shelf (component state) and
// passed in via props; this component maps ids↔names and renders the table + guidance.
const Agencies = ({
    partner,
    orgOptions = {},
    sanAgencyIds: sanAgencyIdsProp = [],
    onChange = () => {},
    isReadonly = false,
    translate = () => {},
}) => {
    const isAgency = useSelector(getIsAgency);
    const agencyOwnId = useSelector(getAgencyId);
    const agencyOwnName = useSelector(state => getUserData(state)?.company_display_name) || '';
    const orgAgencies = useSelector(getOrgAgencies);

    const partnerName = partner?.displayName || '';
    const isSAN = SAN_PARTNERS.includes(partner?.name);
    const sanAgencyIds = getSanAgencyIds(sanAgencyIdsProp);

    // Agencies only ever see their own row (read-only); advertisers see and manage every selected agency.
    const agencyViewingSan = isAgencyViewingSan({
        isAgency,
        isSAN,
        agencyOwnId,
        sanAgencyIds,
        sanViewOnlySupport: orgOptions?.sanPartnersViewOnlyForAgencySupported,
        sanAgencyIdsSupport: !!partner?.sanAgencyIdsSupport,
    });
    const readonly = isReadonly || agencyViewingSan;

    const removeAgency = useCallback(id => onChange(sanAgencyIds.filter(agencyId => agencyId !== id)), [
        onChange,
        sanAgencyIds,
    ]);

    const rows = useMemo(() => {
        // The backend scrubs sanAgencyIds to the viewer's own membership for agencies (an agency only ever
        // receives its own id), so no client-side id filtering is needed — advertisers get the full list.
        const ids = sanAgencyIds;
        const nameSource = agencyViewingSan
            ? [{ agency_id: agencyOwnId, display_name: agencyOwnName }]
            : orgAgencies || [];
        const nameById = new Map(nameSource.map(agency => [agency.agency_id, agency.display_name]));
        return ids.map(id => ({ key: id, id, name: nameById.get(id) || id, prefix: getAgencyCampaignPrefix(id) }));
    }, [sanAgencyIds, orgAgencies, agencyViewingSan, agencyOwnId, agencyOwnName]);

    const metadata = useMemo(
        () => [
            { name: 'name', displayName: translate(`${TRANSLATE_PREFIX}.AGENCY_NAME`), className: css.agencyNameCell },
            { name: 'id', displayName: translate(`${TRANSLATE_PREFIX}.AGENCY_ID`), cellComponent: AgencyIdCell },
            { name: 'prefix', displayName: translate(`${TRANSLATE_PREFIX}.PREFIX`), cellComponent: PrefixCell },
            {
                name: 'actions',
                displayName: translate(`${TRANSLATE_PREFIX}.ACTIONS`),
                headerAlign: 'right',
                cellComponent: ActionsCell,
                cellProps: { onRemove: removeAgency, isReadonly: readonly },
            },
        ],
        [translate, removeAgency, readonly]
    );

    const examplePrefix = getAgencyCampaignPrefix(rows[0]?.id || '');
    const exampleCampaign = `${examplePrefix}${EXAMPLE_CAMPAIGN_SUFFIX}`;

    const warningMessage = (
        <div className={css.warningContent}>
            <div className={css.warningBody}>{translate(`${TRANSLATE_PREFIX}.WARNING_BODY`, { partnerName })}</div>
            <div className={css.example}>
                <div className={css.exampleLabel}>{translate(`${TRANSLATE_PREFIX}.EXAMPLE_LABEL`)}</div>
                <div className={css.exampleValue}>
                    <span className={css.exampleHighlight}>{examplePrefix}</span>
                    {EXAMPLE_CAMPAIGN_SUFFIX}
                </div>
            </div>
        </div>
    );

    return (
        <div className={css.agencies}>
            <WarningMessage
                show
                type="warning"
                collapsible
                defaultExpanded
                showIcon={false}
                collapsibleTitle={`${TRANSLATE_PREFIX}.WARNING_TITLE`}
                message={warningMessage}
            />

            <div className={css.sectionTitle}>{translate(`${TRANSLATE_PREFIX}.SELECTED_AGENCIES`)}</div>
            <div className={css.sectionSubtitle}>
                {translate(`${TRANSLATE_PREFIX}.SELECTED_AGENCIES_SUBTITLE`, { partnerName })}
            </div>

            <Table dataTestId="agencies_table" tableClass={css.table} data={rows} metadata={metadata} />

            <div className={css.howToTitle}>{translate(`${TRANSLATE_PREFIX}.HOW_TO_TITLE`)}</div>
            <ol className={css.howToList}>
                {HOW_TO_STEPS.map(step => (
                    <li key={step}>
                        {translate(`${TRANSLATE_PREFIX}.HOW_TO_STEP_${step}`, { partnerName })}
                        {step === EXAMPLE_STEP && <span className={css.code}>{exampleCampaign}</span>}
                    </li>
                ))}
            </ol>
        </div>
    );
};

Agencies.propTypes = {
    partner: partnerShape,
    orgOptions: PropTypes.shape({ sanPartnersViewOnlyForAgencySupported: PropTypes.bool }),
    sanAgencyIds: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
    isReadonly: PropTypes.bool,
    translate: PropTypes.func,
};

export default withLocalize(Agencies);
